import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { fetchFailedEmbedFiles, retryEmbedFile } from '../../../slice/sharepointSlice'
import { useThemeColor } from '../../../hooks/useThemeColor'
import { useTranslation } from '../../../i18n/LanguageContext'
import { Loader2, RefreshCw, AlertTriangle, CheckCircle2, RotateCcw, FileX } from 'lucide-react'

const MAX_RETRIES = 3

// The backend sends a stable code (not a sentence) for known, translatable
// failure reasons — a real exception's message can't be translated, so it's
// shown as-is, same as before.
function formatEmbedError(error, t) {
  if (error === 'no_text_extracted_scanned_pdf') return t('agentSettings.embedErrorNoTextExtracted')
  return error
}

function RetryBadge({ count }) {
  const left = MAX_RETRIES - count
  if (count >= MAX_RETRIES) return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[#F3F4F6] text-[#9CA3AF]">
      Limit erreicht
    </span>
  )
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-50 text-amber-700">
      {count}/{MAX_RETRIES} Versuche
    </span>
  )
}

function fmt(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })
}

export default function FailedEmbedTab({ taskId = null }) {
  const dispatch = useDispatch()
  const { aispaceColor } = useThemeColor()
  const { t } = useTranslation()
  const { failedEmbedFiles, failedEmbedLoading, failedEmbedError, retryingFileId } = useSelector(s => s.sharepoint)

  useEffect(() => {
    dispatch(fetchFailedEmbedFiles({ task_id: taskId }))
  }, [dispatch, taskId])

  function handleRetry(fileId) {
    dispatch(retryEmbedFile(fileId))
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="text-[15px] font-bold text-[#111827]">Fehlgeschlagene Einbettungen</h3>
          <p className="text-xs text-[#9CA3AF] mt-0.5">
            Dateien, deren Einbettung fehlgeschlagen ist — manuell wiederholen (max. {MAX_RETRIES}×)
          </p>
        </div>
        <button
          onClick={() => dispatch(fetchFailedEmbedFiles({ task_id: taskId }))}
          disabled={failedEmbedLoading}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#374151] bg-[#F3F4F6] hover:bg-[#E5E7EB] rounded-lg disabled:opacity-50"
        >
          {failedEmbedLoading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
          Aktualisieren
        </button>
      </div>

      {failedEmbedError && (
        <div className="mb-4 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-xs text-red-700">
          {failedEmbedError}
        </div>
      )}

      {failedEmbedLoading && failedEmbedFiles.length === 0 ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 size={24} className="animate-spin" style={{ color: aispaceColor }} />
        </div>
      ) : failedEmbedFiles.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <CheckCircle2 size={36} className="text-green-400 mb-3" />
          <p className="text-sm text-[#6B7280]">Keine fehlgeschlagenen Einbettungen</p>
          <p className="text-xs text-[#9CA3AF] mt-1">Alle Dateien wurden erfolgreich eingebettet</p>
        </div>
      ) : (
        <div className="rounded-xl border border-[#E5E7EB] overflow-hidden">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                <th className="px-4 py-3 text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-wide">Datei</th>
                <th className="px-4 py-3 text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-wide">{t('agentSettings.failedEmbedColSiteAgent')}</th>
                <th className="px-4 py-3 text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-wide">Fehler</th>
                <th className="px-4 py-3 text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-wide text-center">Versuche</th>
                <th className="px-4 py-3 text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-wide text-right">Aktion</th>
              </tr>
            </thead>
            <tbody>
              {failedEmbedFiles.map((f) => {
                const isRetrying = retryingFileId === f.id
                const maxed = f.embed_retry_count >= MAX_RETRIES || f.embed_status === 'max_retries'
                return (
                  <tr key={f.id} className={`border-b border-[#F3F4F6] ${maxed ? 'bg-[#FAFAFA]' : 'bg-red-50/20'}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {maxed
                          ? <FileX size={13} className="text-[#9CA3AF] flex-shrink-0" />
                          : <AlertTriangle size={13} className="text-red-500 flex-shrink-0" />}
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-[#374151] truncate max-w-[200px]" title={f.filename}>
                            {f.filename}
                          </p>
                          <p className="text-[10px] text-[#9CA3AF]">{fmt(f.updated_at)}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-xs text-[#374151] truncate max-w-[140px]" title={f.site_name}>
                        {f.site_name || f.sharepoint_site_url || '—'}
                      </p>
                      <p className="text-[10px] text-[#9CA3AF] font-mono truncate max-w-[140px]">
                        Task {f.task_id}
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-[11px] text-red-600 truncate max-w-[200px]" title={formatEmbedError(f.embed_error, t)}>
                        {formatEmbedError(f.embed_error, t) || '—'}
                      </p>
                      {f._retryError && (
                        <p className="text-[11px] text-amber-600 truncate max-w-[200px]" title={f._retryError}>
                          Letzter Fehler: {f._retryError}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <RetryBadge count={f.embed_retry_count || 0} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => handleRetry(f.id)}
                        disabled={isRetrying || maxed}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                          maxed
                            ? 'bg-[#F3F4F6] text-[#9CA3AF] cursor-not-allowed'
                            : 'text-white hover:opacity-90 disabled:opacity-60'
                        }`}
                        style={!maxed ? { backgroundColor: aispaceColor } : {}}
                        title={maxed ? `Maximale Anzahl von ${MAX_RETRIES} Versuchen erreicht` : 'Einbettung erneut versuchen'}
                      >
                        {isRetrying
                          ? <Loader2 size={12} className="animate-spin" />
                          : <RotateCcw size={12} />}
                        {isRetrying ? 'Läuft…' : maxed ? 'Gesperrt' : 'Wiederholen'}
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-4 px-4 py-3 rounded-xl bg-[#FFFBEB] border border-amber-200">
        <p className="text-xs text-amber-700">
          <strong>Hinweis:</strong> Jede Datei kann maximal {MAX_RETRIES}× manuell wiederholt werden.
          Danach ist die Datei gesperrt und muss manuell über SharePoint neu importiert werden.
          Automatische Wiederholungen wurden deaktiviert, um unnötige Azure-Kosten zu vermeiden.
        </p>
      </div>
    </div>
  )
}
